import { defineStore } from 'pinia';
import axios from '../../axios';
import { useCircleStore } from './circle';
import { trackAddToCart, contentId } from '@/analytics/track';

/* The multi-date basket. One entry per delivery date, each with its own lines:
 *   { kind:'package', code, name, portions:[{menu_item_id,name,qty,rice}], soup, rice }
 *   { kind:'item',    variant_id, name, portion, price, qty, rice? }
 *   { kind:'cookware', qty }
 * Prices shown come from the server's quote; the basket only remembers WHAT.
 * Persisted so a reload — or a language switch — never loses it. */
const LS = 'chds_weekly_basket_v1';

// The API returns /media/... paths; media is only served from the API host (the SPA host
// answers those paths with index.html). Same rule the rest of the storefront follows.
export function mediaUrl(path) {
  if (!path) return '';
  return /^https?:\/\//.test(path) ? path : `https://api.chds.com.au${path}`;
}
export function dishFocal(d) {
  const i = (d.item_images || []).find((x) => x.is_main) || (d.item_images || [])[0];
  return i && i.focal_x != null ? `${i.focal_x}% ${i.focal_y}%` : 'center';
}
export function dishFocalPoint(d) {
  const i = (d.item_images || []).find((x) => x.is_main) || (d.item_images || [])[0];
  return i && i.focal_x != null ? { x: i.focal_x, y: i.focal_y } : null;
}
export function portionLabel(p, lang = 'en') {
  let name = String(p.portion_name || '').replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\s+/g, ' ').trim();
  if (lang === 'zh') {
    name = name.replace(/^Meal Set$/i, '餐盒').replace(/^Main Dish$/i, '主菜').replace(/^For\s*(\d+)$/i, '$1 人份');
  }
  const w = String(p.portion_weight || '').trim();
  const wt = !w || w === '0' ? '' : (/^\d+(\.\d+)?$/.test(w) ? `${w}g` : w);
  return wt ? `${name} · ${wt}` : name;
}
/* a "For 2" / "For 4" portion (hot pot sets) is priced and measured per set, not per 100 g */
export function isSetPortion(p) { return /^for\s*\d+/i.test(String((p && p.portion_name) || '')); }
export function macros(p) {
  const n = (v) => Math.round(Number(v) || 0);
  const m = { kcal: n(p && p.calories), protein: n(p && p.protein), carbs: n(p && p.carbs), fat: n(p && p.fats) };
  return (m.kcal || m.protein || m.carbs || m.fat) ? m : null;
}
export function dishImage(d) {
  const i = (d.item_images || []).find((x) => x.is_main) || (d.item_images || [])[0];
  return i ? mediaUrl(i.image) : '';
}

function load() {
  try { return JSON.parse(localStorage.getItem(LS) || 'null') || {}; } catch (e) { return {}; }
}

export const useWeeklyMenuStore = defineStore('weeklyMenu', {
  state: () => ({
    config: null,           // /menus/config/
    dates: [],              // /menus/dates/
    menus: {},              // date -> /menus/dates/<date>/
    activeDate: '',
    deliveries: load(),     // date -> { order_type, address_id, pickup_location_id, time_slot, notes, lines[] }
    quote: null,            // last server quote
    quoteError: '',
    quoteErrorIndex: null,
    quoteErrorField: '',
    removedDates: [],       // [{date, label, count}] dropped because ordering closed
    removedPkgs: [],        // [{date, name}] packages dropped because that day's dish choices changed
    quoting: false,
    loadingConfig: false,
    firstOrderClaim: null,  // { claim_id, verified }
    // Partner co-branded page (chds.com.au/partner/<slug>): branding + venue pick-up +
    // member pricing. While set, every menus/* call carries ?partner=<slug> and the
    // order is attributed to the partner. Cleared when the customer leaves /partner/…
    partner: null,          // /circle/partner/<slug>/page/
    // A partner coupon / Circle referral code typed at checkout (or from the ?ref= cookie).
    code: (() => { try { return localStorage.getItem('chds_weekly_code') || ''; } catch (e) { return ''; } })(),
  }),
  getters: {
    enabled: (s) => !!(s.config && s.config.enabled),
    partnerSlug: (s) => (s.partner && s.partner.slug) || '',
    /** Prefix a weekly-flow path with the partner page when one is active. */
    link: (s) => (path) => {
      if (!s.partner) return path;
      const base = `/partner/${s.partner.slug}`;
      if (path === '/menu') return base;
      if (path === '/packages') return `${base}/packages`;
      if (path === '/checkout-week') return `${base}/checkout`;
      if (path.startsWith('/order/group/')) return `${base}/order/${path.slice('/order/group/'.length)}`;
      return path;
    },
    datesWithLines: (s) => Object.keys(s.deliveries).filter((d) => (s.deliveries[d].lines || []).length).sort(),
    lineCount: (s) => Object.values(s.deliveries).reduce((n, d) => n + (d.lines || []).reduce((m, l) => m + (l.qty || 1), 0), 0),
    packages: (s) => (s.config && s.config.packages) || [],
  },
  actions: {
    persist() { try { localStorage.setItem(LS, JSON.stringify(this.deliveries)); } catch (e) { /* noop */ } },
    _p(extra) { return this.partner ? { ...extra, partner: this.partner.slug } : extra; },
    setCode(code) {
      this.code = String(code || '').trim().toUpperCase();
      try { if (this.code) localStorage.setItem('chds_weekly_code', this.code); else localStorage.removeItem('chds_weekly_code'); } catch (e) { /* noop */ }
    },
    /** typed code (prices the basket) beats the ?ref= cookie (recorded at payment only) */
    _code(forCheckout) {
      if (this.code) return { referral_code: this.code, referral_source: 'explicit' };
      if (!forCheckout) return {};
      try {
        const c = useCircleStore();
        if (c.effectiveCode) return { referral_code: c.effectiveCode, referral_source: 'cookie' };
      } catch (e) { /* optional */ }
      return {};
    },
    async loadPartner(slug) {
      if (this.partner && this.partner.slug === slug) return this.partner;
      const { data } = await axios.get(`/circle/partner/${encodeURIComponent(slug)}/page/`);
      this.partner = data;
      this.config = null;   // the switch may be off for the public but on for this page
      // pick-up at the venue is the default on a partner page
      Object.keys(this.deliveries).forEach((date) => {
        const d = this.deliveries[date];
        if (data.pickup_location && !data.allow_delivery) { d.order_type = 'PICKUP'; d.pickup_location_id = data.pickup_location.id; }
      });
      this.persist();
      return data;
    },
    leavePartner() {
      if (!this.partner) return;
      this.partner = null;
      this.config = null;
      this.quote = null;
    },
    async loadConfig(lang) {
      this.loadingConfig = true;
      try {
        const { data } = await axios.get('/menus/config/', { params: this._p({ lang }) });
        this.config = data;
        return data;
      } finally { this.loadingConfig = false; }
    },
    async loadDates(lang) {
      const { data } = await axios.get('/menus/dates/', { params: this._p({ lang }) });
      this.dates = data.dates || [];
      // A basket lives in the browser for days. Lines for a date whose ordering has closed
      // (cut-off passed, day switched off) block the whole checkout, so take them out and
      // say so, instead of leaving the customer stuck on an error they can't fix.
      if (data.enabled !== false) {
        const open = new Set(this.dates.map((d) => d.date));
        const gone = this.datesWithLines.filter((d) => !open.has(d));
        if (gone.length) {
          gone.forEach((d) => {
            const n = (this.deliveries[d].lines || []).reduce((m, l) => m + (l.qty || 1), 0);
            this.removedDates.push({ date: d, count: n });
            delete this.deliveries[d];
          });
          this.deliveries = { ...this.deliveries };
          this.persist();
        }
      }
      if (!this.activeDate || !this.dates.find((d) => d.date === this.activeDate)) {
        this.activeDate = this.dates[0] ? this.dates[0].date : '';
      }
      return this.dates;
    },
    // A package built days ago can hold a dish the owner has since swapped out of that day's
    // five choices. The server then rejects the WHOLE checkout ("a dish you picked isn't one of
    // the choices"), so drop just that package and tell the customer to re-pick it.
    async dropStalePackages(lang) {
      const dates = this.datesWithLines.filter((dt) => (this.deliveries[dt].lines || []).some((l) => l.kind === 'package'));
      const days = await Promise.all(dates.map((dt) => this.loadMenu(dt, lang).catch(() => null)));   // all days at once
      for (const [i, date] of dates.entries()) {
        const day = days[i];
        if (!day || !this.deliveries[date]) continue;   // can't check: leave it alone
        const ok = new Set((day.package_choices || []).map((x) => x.id));
        if (!ok.size) continue;
        const d = this.deliveries[date];
        const keep = (d.lines || []).filter((l) => {
          if (l.kind !== 'package') return true;
          const bad = (l.portions || []).some((p) => !p.fixed && p.menu_item_id && !ok.has(p.menu_item_id));   // fixed = Small Table veg
          if (bad) this.removedPkgs.push({ date, name: l.name || l.code || 'package', name_zh: l.name_zh || l.name || l.code || '套餐' });
          return !bad;
        });
        if (keep.length !== (d.lines || []).length) {
          if (keep.length) d.lines = keep; else delete this.deliveries[date];
          this.deliveries = { ...this.deliveries };
          this.persist();
        }
      }
    },
    async loadMenu(date, lang) {
      const { data } = await axios.get(`/menus/dates/${date}/`, { params: this._p({ lang }) });
      this.menus = { ...this.menus, [date]: data };
      return data;
    },
    delivery(date) {
      if (!this.deliveries[date]) {
        const p = this.partner;
        const venue = p && p.pickup_location && (!p.allow_delivery || p.allow_pickup) ? p.pickup_location.id : null;
        this.deliveries = { ...this.deliveries, [date]: { order_type: venue && !(p && p.allow_delivery) ? 'PICKUP' : 'DELIVERY',
          address_id: null, pickup_location_id: venue, time_slot: '', notes: '', lines: [] } };
      }
      return this.deliveries[date];
    },
    addLine(date, line) {
      const d = this.delivery(date);
      this._trackAdd(line);
      if (line.kind === 'item') {
        const same = d.lines.find((l) => l.kind === 'item' && l.variant_id === line.variant_id && (l.rice || '') === (line.rice || ''));
        if (same) { same.qty += line.qty || 1; this.persist(); return; }
      }
      if (line.kind === 'cookware') {
        const same = d.lines.find((l) => l.kind === 'cookware');
        if (same) { same.qty += line.qty || 1; this.persist(); return; }
      }
      d.lines.push({ ...line, qty: line.qty || 1, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` });
      this.persist();
    },
    /** One basket line → the {item_id, item_name, price, quantity} shape the pixels use. */
    lineItem(line) {
      const qty = Number(line.qty || 1);
      let price = Number(line.price || 0);
      if (line.kind === 'package') { const p = this.packages.find((x) => x.code === line.code); price = Number((p && p.price) || 0); }
      if (line.kind === 'cookware') price = Number((this.config && this.config.cookware_price) || 0);
      return { item_id: contentId(line), item_name: line.name || (line.kind === 'cookware' ? 'Cookware' : ''), price, quantity: qty };
    },
    /** Every line in the basket, across dates, in pixel shape (+ the basket's own total). */
    basketItems() {
      const items = [];
      this.datesWithLines.forEach((date) => (this.deliveries[date].lines || []).forEach((l) => items.push(this.lineItem(l))));
      return items;
    },
    _trackAdd(line) {
      try {
        const it = this.lineItem(line);
        trackAddToCart({ value: Number((it.price * it.quantity).toFixed(2)), items: [it], contentName: it.item_name,
          meta: { name: it.item_name, id: it.item_id, kind: line.kind } });
      } catch (e) { /* analytics never blocks the basket */ }
    },
    setQty(date, id, qty) {
      const d = this.delivery(date);
      const l = d.lines.find((x) => x.id === id);
      if (!l) return;
      if (qty <= 0) d.lines = d.lines.filter((x) => x.id !== id); else l.qty = qty;
      if (!d.lines.length) delete this.deliveries[date];
      this.persist();
    },
    removeLine(date, id) { this.setQty(date, id, 0); },
    setDeliveryDetails(date, patch) {
      const d = this.delivery(date);
      Object.assign(d, patch);
      this.persist();
    },
    clear() { this.deliveries = {}; this.quote = null; this.persist(); },

    // the shopper's site language, so the server explains a blocked checkout in Chinese too
    _lang() { try { return (localStorage.getItem('chds_lang') || 'en').startsWith('zh') ? 'zh' : 'en'; } catch (e) { return 'en'; } },
    payload() {
      return this.datesWithLines.map((date) => {
        const d = this.deliveries[date];
        return {
          date, order_type: d.order_type, address_id: d.address_id, pickup_location_id: d.pickup_location_id,
          time_slot: d.time_slot, notes: d.notes,
          lines: d.lines.map((l) => {
            if (l.kind === 'package') return { kind: 'package', code: l.code, portions: (l.portions || []).filter((p) => !p.fixed), soup: l.soup, rice: l.rice, client_id: l.id };
            if (l.kind === 'cookware') return { kind: 'cookware', qty: l.qty, client_id: l.id };
            return { kind: 'item', variant_id: l.variant_id, qty: l.qty, rice: l.rice, client_id: l.id };
          }),
        };
      });
    },
    async refreshQuote() {
      if (!this.datesWithLines.length) { this.quote = null; return null; }
      this.quoting = true; this.quoteError = ''; this.quoteErrorIndex = null; this.quoteErrorField = '';
      try {
        const body = this._p({ deliveries: this.payload(), ...this._code(), lang: this._lang() });
        if (this.firstOrderClaim && this.firstOrderClaim.verified) body.first_order_claim_id = this.firstOrderClaim.claim_id;
        const { data } = await axios.post('/menus/quote/', body);
        this.quote = data;
        return data;
      } catch (e) {
        this.quote = null;
        this.quoteError = e.response?.data?.detail || (this._lang() === 'zh' ? '暂时无法计算价格，请检查网络后重试。' : 'Could not price your basket — check your connection and try again.');
        this.quoteErrorIndex = e.response?.data?.delivery_index;
        this.quoteErrorField = e.response?.data?.field || '';
        return null;
      } finally { this.quoting = false; }
    },
    async checkout(opts) {
      // a typed partner/creator code, else the ?ref= cookie, rides along (a partner page wins server-side)
      const body = this._p({ deliveries: this.payload(), ...this._code(true), ...opts, lang: this._lang() });
      if (this.firstOrderClaim && this.firstOrderClaim.verified) body.first_order_claim_id = this.firstOrderClaim.claim_id;
      const { data } = await axios.post('/menus/checkout/', body);
      return data;
    },
    async firstOrderCheck(phone, email) {
      const { data } = await axios.post('/menus/first-order/check/', { phone, email });
      if (data.eligible) this.firstOrderClaim = { claim_id: data.claim_id, verified: !data.verify, needsCode: !!data.verify };
      else this.firstOrderClaim = null;
      return data;
    },
    async firstOrderVerify(code) {
      const { data } = await axios.post('/menus/first-order/verify/', { claim_id: this.firstOrderClaim.claim_id, code });
      if (data.verified) this.firstOrderClaim = { ...this.firstOrderClaim, verified: true, needsCode: false };
      return data;
    },
  },
});
